import React, { useState } from 'react';
import { CheckCircle, AlertCircle, Send, ShieldCheck } from 'lucide-react';
import { COMPANY_INFO } from '../data';

export default function FormSection({ title = "Book et gratis besøg", subtitle = "Find ud af hvor meget I kan spare, samt hvilken løsning der passer bedst til jer." }) {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    description: '',
    website_url: '' // Honeypot field
  });

  const [status, setStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic client-side validation
    if (!formData.name || !formData.phone || !formData.email) {
      setStatus('error');
      setErrorMessage('Venligst udfyld alle påkrævede felter (Navn, Telefon og Email).');
      return;
    }

    try {
      setStatus('submitting');
      
      // Submit to the webform endpoint
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          Navn: formData.name,
          Telefon: formData.phone,
          "E-mail": formData.email,
          Meddelelse: formData.description,
          website_url: formData.website_url // Honeypot field
        })
      });

      if (!response.ok) {
        throw new Error('Der opstod en fejl under afsendelsen. Prøv venligst igen.');
      }

      const result = await response.json();
      if (result.status === 'ok' || response.status === 200) {
        setStatus('success');
        setFormData({ name: '', phone: '', email: '', description: '', website_url: '' });
      } else {
        throw new Error('Valideringsfejl fra serveren.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setStatus('error');
      setErrorMessage(error.message || 'Der opstod en netværksfejl. Kontroller din forbindelse og prøv igen.');
    }
  };

  if (status === 'success') {
    return (
      <div className="bg-white rounded-2xl p-6 sm:p-8 border border-brand-light shadow-xl text-center space-y-6 animate-fade-in">
        <div className="inline-flex items-center justify-center p-3 bg-brand-light rounded-full text-brand-green border border-brand-green/20">
          <CheckCircle className="w-12 h-12" />
        </div>
        <div className="space-y-2">
          <h3 className="text-xl sm:text-2xl font-bold text-brand-deep">Tak for din henvendelse!</h3>
          <p className="text-sm sm:text-base text-brand-dark/70">
            Vi har modtaget din booking. Vi kontakter dig hurtigst muligt inden for 24 timer for at aftale tidspunktet for dit gratis besøg.
          </p>
        </div>
        <div className="pt-4 border-t border-brand-light text-xs text-brand-dark/50 flex items-center justify-center gap-1.5">
          <ShieldCheck className="w-4 h-4 text-brand-green" />
          Dine data behandles fortroligt
        </div>
      </div>
    );
  }

  return (
    <div className="relative bg-white rounded-2xl p-6 sm:p-8 border border-brand-light shadow-xl space-y-6">
      {/* Organic Blue Badge */}
      <div className="absolute -top-10 -right-8 sm:-right-14 w-36 h-36 sm:w-44 sm:h-44 z-10 drop-shadow-lg animate-hop">
        <svg viewBox="0 0 200 200" className="w-full h-full text-brand-blue fill-current">
          <path d="M165,100 C165,135 135,165 100,165 C65,165 35,135 35,100 C35,65 65,35 100,35 C135,35 165,65 165,100 Z" />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center text-white p-3">
          <span className="text-[10px] sm:text-xs uppercase tracking-widest font-extrabold text-brand-light/90">Slå til nu</span>
          <span className="text-base sm:text-lg font-black leading-none my-1">OG SPAR</span>
          <span className="text-3xl sm:text-4xl font-black leading-none text-yellow-300 drop-shadow-sm">20%</span>
        </div>
      </div>

      <div className="space-y-2 pr-16 sm:pr-24">
        <h3 className="text-xl sm:text-2xl font-bold text-brand-deep tracking-tight">
          {title}
        </h3>
        <p className="text-sm text-brand-dark/70 leading-relaxed">
          {subtitle}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Honeypot Field (Absolutely Hidden) */}
        <div className="sr-only absolute -top-[9999px] -left-[9999px]">
          <label htmlFor="website_url">Udfyld ikke dette felt</label>
          <input
            type="text"
            id="website_url"
            name="website_url"
            value={formData.website_url}
            onChange={handleChange}
            tabIndex="-1"
            autoComplete="off"
          />
        </div>

        {/* Name */}
        <div>
          <label htmlFor="name" className="block text-xs font-semibold text-brand-deep uppercase tracking-wider mb-1.5">
            Navn <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            required
            value={formData.name}
            onChange={handleChange}
            placeholder="Dit fulde navn"
            className="w-full px-4 py-3 rounded-lg border border-brand-light bg-brand-warm/30 text-brand-dark placeholder-brand-dark/40 focus:outline-none focus:border-brand-blue focus:ring-1 focus:ring-brand-blue transition-all text-sm sm:text-base"
          />
        </div>

        {/* Phone */}
        <div>
          <label htmlFor="phone" className="block text-xs font-semibold text-brand-deep uppercase tracking-wider mb-1.5">
            Telefonnummer <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            required
            value={formData.phone}
            onChange={handleChange}
            placeholder="F.eks. 21 82 80 00"
            className="w-full px-4 py-3 rounded-lg border border-brand-light bg-brand-warm/30 text-brand-dark placeholder-brand-dark/40 focus:outline-none focus:border-brand-blue focus:ring-1 focus:ring-brand-blue transition-all text-sm sm:text-base"
          />
        </div>

        {/* Email */}
        <div>
          <label htmlFor="email" className="block text-xs font-semibold text-brand-deep uppercase tracking-wider mb-1.5">
            E-mailadresse <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            required
            value={formData.email}
            onChange={handleChange}
            placeholder="din@email.dk"
            className="w-full px-4 py-3 rounded-lg border border-brand-light bg-brand-warm/30 text-brand-dark placeholder-brand-dark/40 focus:outline-none focus:border-brand-blue focus:ring-1 focus:ring-brand-blue transition-all text-sm sm:text-base"
          />
        </div>

        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-xs font-semibold text-brand-deep uppercase tracking-wider mb-1.5">
            Beskrivelse af opgaven
          </label>
          <textarea
            id="description"
            name="description"
            rows="3"
            value={formData.description}
            onChange={handleChange}
            placeholder="Fortæl os kort om dine vinduer eller ruder (f.eks. antal, type eller om de er punkterede)..."
            className="w-full px-4 py-3 rounded-lg border border-brand-light bg-brand-warm/30 text-brand-dark placeholder-brand-dark/40 focus:outline-none focus:border-brand-blue focus:ring-1 focus:ring-brand-blue transition-all text-sm sm:text-base resize-none"
          />
        </div>

        {/* Error Message */}
        {status === 'error' && (
          <div className="flex items-start gap-2.5 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs sm:text-sm">
            <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
            <span>{errorMessage}</span>
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="w-full bg-brand-blue hover:bg-brand-deep disabled:bg-brand-blue/50 text-white font-bold py-3.5 px-6 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg flex items-center justify-center gap-2 text-sm sm:text-base cursor-pointer"
        >
          {status === 'submitting' ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
              <span>Sender henvendelse...</span>
            </>
          ) : (
            <>
              <Send className="w-4 h-4" />
              <span>Book gratis besøg nu</span>
            </>
          )}
        </button>
      </form>

      <div className="pt-4 border-t border-brand-light flex items-center justify-center gap-1.5 text-xs text-brand-dark/50">
        <ShieldCheck className="w-4 h-4 text-brand-green" />
        <span>Gratis og uforpligtende rådgivning</span>
      </div>
    </div>
  );
}
